<template>
    <div id="main">
        <loader v-if="isLoading && !$store.getters.isLoading" />
        <div class="page">
            <div class="vertical-flow">
                <div class="listing-header">
                    <!-- TÍTULO E BREADCRUMB -->
                    <div class="title-breadcrumb">
                        <h1 class="title">{{ $t("contas-receber") }}</h1>
                        <nav class="breadcrumb" aria-label="breadcrumbs">
                            <ul>
                                <li>
                                    <a class="a-inativo">{{
                                        $t("vendas")
                                    }}</a>
                                </li>
                                <li>
                                    <a class="a-inativo">{{
                                        $t("financeiro")
                                    }}</a>
                                </li>
                                <li>
                                    <a aria-current="page">{{
                                        $t("contas-receber")
                                    }}</a>
                                </li>
                            </ul>
                        </nav>
                    </div>
                </div>

                <!-- PESQUISA E FILTROS -->
                <div class="filters-bar mb-3">
                    <div class="filters-bar-form form-columns-fit">
                        <div class="columns">
                            <!-- Data Inicio-->
                            <div class="column is-4">
                                <label v-text="$t('mes-inicio')"/>
                                <b-datepicker
                                    class="dpicker-filter"
                                    type="month"
                                    :month-names="meses"
                                    :date-formatter="(date) => $moment(date).format('MM/YYYY')"
                                    v-model="mes.ini"
                                    :mobile-native="false"
                                    icon="calendar-today"
                                    :years-range="[-1, 8]"
                                    :max-date="mes.fim"
                                    @input="carregarDados()"
                                />
                            </div>
                            <!-- Data Fim-->
                            <div class="column is-4">
                                <label v-text="$t('mes-fim')"/>
                                <b-datepicker
                                    class="dpicker-filter"
                                    type="month"
                                    :month-names="meses"
                                    :date-formatter="(date) => $moment(date).format('MM/YYYY')"
                                    v-model="mes.fim"
                                    :mobile-native="false"
                                    icon="calendar-today"
                                    :years-range="[-2, 8]"
                                    :min-date="mes.ini"
                                    :max-date="new Date()"
                                    @input="carregarDados()"
                                />
                            </div>
                        </div>
                    </div>
                    <div class="filters-bar-actions">
                        <b-button
                            class="ml-2"
                            type="is-primary"
                            icon-right="format-list-bulleted"
                            @click="$router.push(
                                { name: 'ven-contas-receber-list' }
                            )"
                            v-tippy 
                            :title="$t('lista')"
                        />
                    </div>
                </div>

                <!-- CHARTS -->
                <empty-table-message v-if="isEmpty" />
                <div class="registry-wrapper" v-else >
                    <div class="columns mt-2">
                        <div class="column is-4">
                            <div class="card">
                                <header class="card-header">
                                    <p class="card-header-title">
                                        {{ $t("total-contas-receber") }}
                                    </p>
                                </header>
                                <div class="card-content">
                                    <div class="content">
                                        {{ card.totalContasReceber | decimal(null, true) }}
                                    </div>
                                </div>
                                <footer class="card-footer">
                                    <p class="card-footer-item left is-size-7">
                                        {{ card.qtdContasReceber }} {{ $t('titulos') }}
                                    </p>
                                    <p class="card-footer-item right is-size-7">
                                        {{ 100 | decimal(0) }}%
                                    </p>
                                </footer>
                            </div>
                        </div>
                        <div class="column is-4">
                            <div class="card">
                                <header class="card-header">
                                    <p class="card-header-title">
                                        {{ $t("total-contas-receber-vencidas") }}
                                    </p>
                                </header>
                                <div class="card-content">
                                    <div class="content">
                                        {{ card.totalContasReceberVencidas | decimal(null, true) }}
                                    </div>
                                </div>
                                <footer class="card-footer">
                                    <p class="card-footer-item left is-size-7">
                                        {{ card.qtdContasReceberVencidas }} {{ $t('titulos') }}
                                    </p>
                                    <p class="card-footer-item right is-size-7">
                                        {{ calculaPercentual(card.qtdContasReceberVencidas) }}%
                                    </p>
                                </footer>
                            </div>
                        </div>
                        <div class="column is-4">
                            <div class="card">
                                <header class="card-header">
                                    <p class="card-header-title">
                                        {{ $t("total-contas-receber-vencer") }}
                                    </p>
                                </header>
                                <div class="card-content">
                                    <div class="content">
                                        {{ card.totalContasReceberVencer | decimal(null, true) }}
                                    </div>
                                </div>
                                <footer class="card-footer">
                                    <p class="card-footer-item left is-size-7">
                                        {{ card.qtdContasReceberVencer }} {{ $t('titulos') }}
                                    </p>
                                    <p class="card-footer-item right is-size-7" >
                                        {{ calculaPercentual(card.qtdContasReceberVencer) }}%
                                    </p>
                                </footer>
                            </div>
                        </div>
                    </div>
                    <div class="columns">
                        <div class="column">
                            <div class="chart-content mt-1">
                                <div class="chart">
                                    <apexchart
                                        v-if="!isChartLoading"
                                        type="bar"
                                        :options="chartOptions"
                                        :series="chartSeries"
                                        :height="525"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import ContasReceberService from "@/services/vendas/financeiro/ContasReceberService";
// charts
import { palette } from '@/functions/color/Palette.js';

export default {
    data() {
        return {
            service: new ContasReceberService(this.$http, this.$router),

            isLoading: false,
            isComplete: false,
            isChartLoading: false,
            permissao: {},
            
            meses: [
                this.$t('january'), 
                this.$t('february'), 
                this.$t('march'), 
                this.$t('april'), 
                this.$t('may'), 
                this.$t('june'),
                this.$t('july'), 
                this.$t('august'), 
                this.$t('september'), 
                this.$t('october'), 
                this.$t('november'), 
                this.$t('december')
            ],
            mes: {
                ini: this.$moment().subtract(2, 'month').toDate(),
                fim: this.$moment().toDate()
            },

            isEmpty: false,
            card: {
                totalContasReceber: 0,
                totalContasReceberVencidas: 0,
                totalContasReceberVencer: 0,
                qtdContasReceber: 0,
                qtdContasReceberVencidas: 0,
                qtdContasReceberVencer: 0,
                dataReferencia: null,
            },
            chartSeries: [],
            chartOptions: {}
        };
    },
    created() {
        this.mes.ini = this.$route.params.mesInicio
        this.mes.fim = this.$route.params.mesFim

        this.$cookies.set('ven-contas-receber', 'chart');

        this.permissao = this.hasPermissionAccess(
            "vendas",
            "financeiro",
            "ven-contas-receber"
        );

        !this.permissao.read &&
            this.$router.push({
                name: "acesso-nao-permitido",
                params: { tela: "contas-receber" },
            });

        this.carregarDados();
    },
    methods: {
        carregarDados() {
            // cria em texto para não dar errod e referência
            let iniStart = this.$moment(this.mes.ini).startOf('month').format('YYYY-MM-DD');
            let fimEnd = this.$moment(this.mes.fim).endOf('month').format('YYYY-MM-DD');

            this.isLoading = true;

            this.service
                .findTotal(iniStart, fimEnd)
                .then((body) => {
                    if(body.content && body.content.length > 0) {
                        const cardData = body.content[0];
                        
                        this.card = cardData;
                        
                        const chartData = body.content.slice(1);

                        if(cardData.totalContasReceber === 0) return this.chartSeries = [];

                        const chartLabelsAndDatas = this.mapToChartData(chartData);

                        const allChartData = [
                            ...chartLabelsAndDatas.totalContasReceberList,
                            ...chartLabelsAndDatas.totalContasReceberVencidasList,
                            ...chartLabelsAndDatas.totalContasReceberVencerList
                        ];

                        this.chartOptions = this.configureChartOptions(chartLabelsAndDatas.meses, allChartData);

                        this.chartSeries = this.mapToSeries(chartLabelsAndDatas);
                    } else { 
                        this.isEmpty = true;
                        this.$root.showMessage("error", "msg-erro-load");
                    }
                })
                .catch((e) => {
                    console.error(e);
                    this.$root.showMessage("error", "msg-erro-load");
                })
                .finally((_) => {
                    this.isLoading = false;
                });
        },
        calculaPercentual(valor){
            // porcentagem de um valor x quando você sabe que o total é Y
            // X = (nummero de registros de qual vc quer saber a porcentagem * 100) / total de registros
            let temp = (valor*100) / this.card.qtdContasReceber;
            if(temp == null) return 0;
            return this.getDecimal(temp, 0);
        },
        mapToChartData(totaisPorMes) {
            return totaisPorMes.reduce((previous, current) => {
                previous.totalContasReceberList.push(current.totalContasReceber)
                previous.totalContasReceberVencidasList.push(current.totalContasReceberVencidas)
                previous.totalContasReceberVencerList.push(current.totalContasReceberVencer)

                previous.qtdeContasReceberList.push(current.qtdContasReceber)
                previous.qtdeContasReceberVencidasList.push(current.qtdContasReceberVencidas)
                previous.qtdeContasReceberVencerList.push(current.qtdContasReceberVencer)

                previous.meses.push(current.nome)

                return previous;
            }, {
                totalContasReceberList: [],
                totalContasReceberVencidasList: [],
                totalContasReceberVencerList: [],
                qtdeContasReceberList: [],
                qtdeContasReceberVencidasList: [],
                qtdeContasReceberVencerList: [],
                meses: []
            })
        },
        configureChartOptions(chartLabels, allChartData) {
            const labelsStringToDate = chartLabels.map(chart => {
                const [month, year] = chart.split('/').map(Number);

                return new Date(year, month - 1, 1).getTime();
            });

            const outterContext = this;

            const tooltipFormatter = function(value, { seriesIndex, dataPointIndex }) {
                return `${outterContext.parseAsCurrency(value)} | Quantidade: ${outterContext.chartSeries[seriesIndex].qtde[dataPointIndex]}`
            }

            const stepSize = this.evaluateStepSizeByMaxValue(allChartData);

            const labelYAxisFormatter = (labelYAxis) => {
                if(labelYAxis >= 1000) return labelYAxis / 1000  + 'K'
                if(labelYAxis >= 1000000) return labelYAxis / 1000000  + 'M'

                return labelYAxis
            }

            return {
                chart: {
                    type: 'bar',
                },
                xaxis: {
                    type: 'datetime',
                    categories: labelsStringToDate,
                    labels: {
                        datetimeFormatter: {
                            year: 'MM/yyyy',
                            month: 'MM/yyyy',
                            day: '',
                        }
                    },
                },
                yaxis: {
                    stepSize,
                    labels: {
                        formatter: labelYAxisFormatter
                    }
                },
                tooltip: {
                    shared: true,
                    intersect: false,
                    y: {
                        formatter: tooltipFormatter
                    },
                    x: {
                        format: 'MM/yyyy',
                    }
                },
                colors: palette.getChartColors(),
                plotOptions: {
                    bar: {
                        dataLabels: {
                            position: 'top',
                        },
                    }
                },
                dataLabels: {
                    enabled: false,
                },
                stroke: {
                    show: true,
                    width: 1,
                    colors: ['#fff']
                },
                legend: {
                    fontSize: '16px',
                    position: 'top'
                },
                noData: {
                    text: this.$t('chart-no-data'),
                    align: 'center',
                    verticalAlign: 'middle',
                    offsetX: 0,
                    offsetY: 0,
                    style: {
                        fontSize: '14px',
                    }
                },
                toolbar: {
                    show: true,
                    offsetX: 0,
                    offsetY: 0,
                    tools: {
                        download: true,
                        selection: true,
                        zoom: true,
                        zoomin: true,
                        zoomout: true,
                        pan: true,
                        reset: 1,
                    },
                    autoSelected: 'zoom'
                },
            }
        },
        mapToSeries(chartLabelsAndDatas) {
            return [
                {
                    name: this.$t("total-contas-receber"),
                    data: chartLabelsAndDatas.totalContasReceberList,
                    qtde: chartLabelsAndDatas.qtdeContasReceberList
                },
                {
                    name: this.$t("total-contas-receber-vencidas"),
                    data: chartLabelsAndDatas.totalContasReceberVencidasList,
                    qtde: chartLabelsAndDatas.qtdeContasReceberVencidasList
                },
                {
                    name: this.$t("total-contas-receber-vencer"),
                    data: chartLabelsAndDatas.totalContasReceberVencerList,
                    qtde: chartLabelsAndDatas.qtdeContasReceberVencerList
                }
            ]
        },
        evaluateStepSizeByMaxValue(chartData) {
            const maxValue = Math.max(...chartData);

            switch(true) {
                case maxValue < 2500: return 100
                case maxValue < 10000: return 500
                case maxValue < 25000: return 1000
                case maxValue < 100000: return 5000
            } 
        },
    },
};
</script>

<style lang="scss">
.chart {
    background-color: #fff;
    border-radius: 5px;
    -webkit-box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.2);
    box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.2);
    padding-top: 5px;
}

#main {
    .page {
        td:has(.label-situacao) {
            padding-left: 0;
        }
    }
}
</style>

<style lang="scss" scoped>
#main {
    padding: 0 20px;
    height: 100%;
    .page {
        position: relative;
        padding-left: 0;
    
        // Barra de filtro
        .filters-bar {
            display: flex;
            .filters-bar-form {
                flex-grow: 1;
            }
            .filters-bar-actions {
                display: flex;
                padding-bottom: 2px;
                align-items: stretch;
            }
    
            @media screen and (min-width: 851px) {
                .filters-bar-actions {
                    align-self: flex-end;
                    margin-left: 10px;
                }
            }
            @media screen and (max-width: 850px) {
                flex-direction: column;
                .filters-bar-actions {
                    align-self: center;
                    margin-top: 10px;
                }
            }
        }

        // Card
        // .card-footer-item.left {
        //     justify-content: left ;
        // }
        // .card-footer-item.right {
        //     justify-content: right ;
        // }
        .card-content {
            .content {
                color: var(--primary-bn-color);
                font-weight: bold;
                text-align: center;
                font-size: 3vw;
            }
        }
    }
}

</style>